import { FC } from 'react';

import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import DesktopFeatureTable from 'components/Tables/FeatureComparisonTable/components/DesktopFeatureTable';
import FlexibleDesktopTable from 'components/Tables/FeatureComparisonTable/components/FlexibleDesktopTable';
import FlexibleMobileTable from 'components/Tables/FeatureComparisonTable/components/FlexibleMobileTable';
import MobileFeatureTable from 'components/Tables/FeatureComparisonTable/components/MobileFeatureTable';
import { FeatureComparisonTableConfigProps } from 'components/Tables/FeatureComparisonTable/config';

import { hasArrayValues } from 'utils/array/arrays';

const FeatureComparisonTable: FC<FeatureComparisonTableConfigProps> = props => {
  const {
    sectionProps,
    rest: { tableHeading, tableSections, columns, rows, ...details },
  } = splitSectionProps(props);

  // Content saved before the editable Columns/Rows layout keeps rendering exactly as it always has
  const hasClassicRows = hasArrayValues(tableSections) && tableSections.some(section => hasArrayValues(section.rows));

  if (hasClassicRows) {
    return (
      <Section {...sectionProps}>
        <ScrollFadeSection delay={0.1}>
          <div className="flex w-full flex-col gap-4">
            <div className="hidden lg:block">
              <DesktopFeatureTable tableHeading={tableHeading} tableSections={tableSections} {...details} />
            </div>

            <div className="lg:hidden">
              <MobileFeatureTable tableHeading={tableHeading} tableSections={tableSections} {...details} />
            </div>
          </div>
        </ScrollFadeSection>
      </Section>
    );
  }

  if (!hasArrayValues(columns)) {
    return null;
  }

  // Builder fills the ids through onChange, but pasted content may not have them
  const safeColumns = columns.map((column, idx) => ({ ...column, id: column.id || `col-${idx}` }));
  const safeRows = (rows ?? []).map((row, idx) => ({ ...row, id: row.id || `row-${idx}` }));

  return (
    <Section {...sectionProps}>
      <ScrollFadeSection delay={0.1}>
        <div className="w-full">
          <div className="hidden lg:block">
            <FlexibleDesktopTable tableHeading={tableHeading} columns={safeColumns} rows={safeRows} />
          </div>

          <div className="lg:hidden">
            {tableHeading && (
              <h2 className="mb-6 font-spaceGrotesk text-[24px] font-medium leading-[32px] text-white">
                {tableHeading}
              </h2>
            )}
            <FlexibleMobileTable columns={safeColumns} rows={safeRows} />
          </div>
        </div>
      </ScrollFadeSection>
    </Section>
  );
};

export default FeatureComparisonTable;
